import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import type { ReferenceUpdatePlan } from "../../api/dtoolsApi";
import { toastEmitter } from "../../utils/toastEmitter";
import { getErrorMessage } from "../../utils/errorHandling";
import type translations from "../../translations/translations.json";

type TranslationKeys = keyof typeof translations.en;

export type DedupeEntityBase = {
  _id: string;
};

type FieldConfig<TEntity extends DedupeEntityBase, TFields extends object> = {
  key: keyof TFields & string;
  label: string;
  getValue: (entity: TEntity) => unknown;
};

type CandidateGroup<TEntity extends DedupeEntityBase> = {
  score: number;
  items: TEntity[];
};

type MergePlan = {
  willDeleteId: string;
  referenceUpdates: ReferenceUpdatePlan[];
  extraSummary?: Array<{ label: string; value: string | number }>;
};

export type DedupeEntityPanelProps<TEntity extends DedupeEntityBase, TFields extends object> = {
  entityLabel: string;
  loadCandidates: () => Promise<{
    groups: CandidateGroup<TEntity>[];
    allItems: TEntity[];
  }>;
  getOptionLabel: (entity: TEntity) => string;
  getChipLabel: (entity: TEntity) => string;
  fields: Array<FieldConfig<TEntity, TFields>>;
  fieldsFromEntity: (entity: TEntity) => TFields;
  identityNote: string;
  dryRun: (payload: {
    keepId: string;
    discardId: string;
    fields: TFields;
  }) => Promise<{ dryRunToken: string; plan: MergePlan }>;
  apply: (payload: {
    keepId: string;
    discardId: string;
    fields: TFields;
    dryRunToken: string;
  }) => Promise<void>;
  successMessage: string;
  readOnly?: boolean;
};

const formatFieldValue = (value: unknown): string => {
  if (value === null || value === undefined || value === "") {
    return "—";
  }
  if (typeof value === "boolean") {
    return value ? "true" : "false";
  }
  if (typeof value === "object") {
    return JSON.stringify(value);
  }
  return String(value);
};

function DedupeEntityPanel<TEntity extends DedupeEntityBase, TFields extends object>({
  entityLabel,
  loadCandidates,
  getOptionLabel,
  getChipLabel,
  fields,
  fieldsFromEntity,
  identityNote,
  dryRun,
  apply,
  successMessage,
  readOnly = false,
}: DedupeEntityPanelProps<TEntity, TFields>) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [groups, setGroups] = useState<CandidateGroup<TEntity>[]>([]);
  const [allItems, setAllItems] = useState<TEntity[]>([]);
  const [keepItem, setKeepItem] = useState<TEntity | null>(null);
  const [discardItem, setDiscardItem] = useState<TEntity | null>(null);
  const [mergeFields, setMergeFields] = useState<TFields | null>(null);
  const [fieldSources, setFieldSources] = useState<
    Partial<Record<keyof TFields & string, "keep" | "discard">>
  >({});
  const [dryRunToken, setDryRunToken] = useState<string | null>(null);
  const [plan, setPlan] = useState<MergePlan | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await loadCandidates();
      setGroups(result.groups);
      setAllItems(result.allItems);
    } catch (err) {
      setError(getErrorMessage(err, t));
    } finally {
      setLoading(false);
    }
  }, [loadCandidates, t]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const resetDryRun = () => {
    setDryRunToken(null);
    setPlan(null);
  };

  const selectPair = (keep: TEntity, discard: TEntity) => {
    setKeepItem(keep);
    setDiscardItem(discard);
    setMergeFields(fieldsFromEntity(keep));
    setFieldSources(
      Object.fromEntries(fields.map(({ key }) => [key, "keep"])) as Record<
        keyof TFields & string,
        "keep" | "discard"
      >,
    );
    resetDryRun();
  };

  const handleFieldSourceChange = (key: keyof TFields & string, source: "keep" | "discard") => {
    if (!keepItem || !discardItem) {
      return;
    }
    const sourceEntity = source === "keep" ? keepItem : discardItem;
    const field = fields.find((item) => item.key === key);
    if (!field) {
      return;
    }
    setFieldSources((prev) => ({ ...prev, [key]: source }));
    setMergeFields((prev) =>
      prev
        ? {
            ...prev,
            [key]: field.getValue(sourceEntity),
          }
        : prev,
    );
    resetDryRun();
  };

  const swapKeepDiscard = () => {
    if (!keepItem || !discardItem) {
      return;
    }
    setKeepItem(discardItem);
    setDiscardItem(keepItem);
    setFieldSources((prev) => {
      const swapped: Partial<Record<keyof TFields & string, "keep" | "discard">> = {};
      for (const { key } of fields) {
        const current = prev[key] ?? "keep";
        swapped[key] = current === "keep" ? "discard" : "keep";
      }
      return swapped;
    });
    resetDryRun();
  };

  const canMerge = useMemo(
    () => Boolean(keepItem && discardItem && mergeFields),
    [keepItem, discardItem, mergeFields],
  );

  const handleDryRun = async () => {
    if (!keepItem || !discardItem || !mergeFields) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await dryRun({
        keepId: keepItem._id,
        discardId: discardItem._id,
        fields: mergeFields,
      });
      setDryRunToken(result.dryRunToken);
      setPlan(result.plan);
      toastEmitter.emit(t("Dry-run completed" as TranslationKeys), "success");
    } catch (err) {
      setDryRunToken(null);
      setPlan(null);
      setError(getErrorMessage(err, t));
    } finally {
      setBusy(false);
    }
  };

  const handleApply = async () => {
    if (!keepItem || !discardItem || !mergeFields || !dryRunToken) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await apply({
        keepId: keepItem._id,
        discardId: discardItem._id,
        fields: mergeFields,
        dryRunToken,
      });
      toastEmitter.emit(successMessage, "success");
      setKeepItem(null);
      setDiscardItem(null);
      setMergeFields(null);
      setFieldSources({});
      resetDryRun();
      await refresh();
    } catch (err) {
      setError(getErrorMessage(err, t));
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Stack spacing={3}>
      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", sm: "center" }}
          spacing={1}
          sx={{ mb: 2 }}
        >
          <Typography variant="h6">{t("Detected duplicates" as TranslationKeys)}</Typography>
          <Button variant="outlined" onClick={() => void refresh()} disabled={busy}>
            {t("Refresh" as TranslationKeys)}
          </Button>
        </Stack>

        {groups.length === 0 ? (
          <Alert severity="info">
            {t("No likely duplicates detected." as TranslationKeys)} ({entityLabel})
          </Alert>
        ) : (
          <Stack spacing={1.5}>
            {groups.map((group) => (
              <Paper
                key={group.items.map((item) => item._id).join("|")}
                variant="outlined"
                sx={{
                  p: 1.5,
                  cursor: readOnly ? "default" : "pointer",
                  opacity: readOnly ? 0.85 : 1,
                  "&:hover": readOnly ? undefined : { borderColor: "primary.main" },
                }}
                onClick={() => {
                  if (readOnly) {
                    return;
                  }
                  if (group.items.length >= 2) {
                    selectPair(group.items[0], group.items[1]);
                  }
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Chip size="small" label={`score ${group.score}`} color="warning" />
                  {group.items.map((item) => (
                    <Chip
                      key={item._id}
                      size="small"
                      variant="outlined"
                      label={getChipLabel(item)}
                    />
                  ))}
                </Stack>
              </Paper>
            ))}
          </Stack>
        )}
      </Paper>

      {!readOnly && (
        <>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              {t("Select entities to merge" as TranslationKeys)}
            </Typography>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <Autocomplete
                options={allItems}
                getOptionLabel={getOptionLabel}
                value={keepItem}
                onChange={(_event, value) => {
                  if (!value) {
                    setKeepItem(null);
                    resetDryRun();
                    return;
                  }
                  if (discardItem && discardItem._id === value._id) {
                    setError(t("Keep and discard must be different" as TranslationKeys));
                    return;
                  }
                  setKeepItem(value);
                  setMergeFields(fieldsFromEntity(value));
                  setFieldSources(
                    Object.fromEntries(fields.map(({ key }) => [key, "keep"])) as Record<
                      keyof TFields & string,
                      "keep" | "discard"
                    >,
                  );
                  resetDryRun();
                }}
                renderInput={(params) => (
                  <TextField {...params} label={t("Keep" as TranslationKeys)} />
                )}
                sx={{ flex: 1 }}
              />
              <Autocomplete
                options={allItems}
                getOptionLabel={getOptionLabel}
                value={discardItem}
                onChange={(_event, value) => {
                  if (!value) {
                    setDiscardItem(null);
                    resetDryRun();
                    return;
                  }
                  if (keepItem && keepItem._id === value._id) {
                    setError(t("Keep and discard must be different" as TranslationKeys));
                    return;
                  }
                  setDiscardItem(value);
                  resetDryRun();
                }}
                renderInput={(params) => (
                  <TextField {...params} label={t("Discard" as TranslationKeys)} />
                )}
                sx={{ flex: 1 }}
              />
            </Stack>
            {keepItem && discardItem && (
              <Button sx={{ mt: 2 }} onClick={swapKeepDiscard} disabled={busy}>
                {t("Swap keep / discard" as TranslationKeys)}
              </Button>
            )}
          </Paper>

          {canMerge && keepItem && discardItem && mergeFields && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                {t("Choose final field values" as TranslationKeys)}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {identityNote}
              </Typography>
              <Stack spacing={2} divider={<Divider flexItem />}>
                {fields.map(({ key, label, getValue }) => (
                  <FormControl key={key} component="fieldset">
                    <FormLabel component="legend">{t(label as TranslationKeys)}</FormLabel>
                    <RadioGroup
                      value={fieldSources[key] ?? "keep"}
                      onChange={(event) =>
                        handleFieldSourceChange(key, event.target.value as "keep" | "discard")
                      }
                    >
                      <FormControlLabel
                        value="keep"
                        control={<Radio />}
                        label={`${getChipLabel(keepItem)}: ${formatFieldValue(getValue(keepItem))}`}
                      />
                      <FormControlLabel
                        value="discard"
                        control={<Radio />}
                        label={`${getChipLabel(discardItem)}: ${formatFieldValue(getValue(discardItem))}`}
                      />
                    </RadioGroup>
                  </FormControl>
                ))}
              </Stack>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mt: 3 }}>
                <Button variant="outlined" onClick={() => void handleDryRun()} disabled={busy}>
                  {busy ? <CircularProgress size={20} /> : t("Dry-run merge" as TranslationKeys)}
                </Button>
                <Button
                  variant="contained"
                  color="warning"
                  onClick={() => void handleApply()}
                  disabled={busy || !dryRunToken}
                >
                  {t("Apply merge" as TranslationKeys)}
                </Button>
              </Stack>
              {!dryRunToken && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  {t(
                    "You must run a successful dry-run before applying the merge." as TranslationKeys,
                  )}
                </Alert>
              )}
            </Paper>
          )}

          {plan && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                {t("Dry-run plan" as TranslationKeys)}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {t("Will delete" as TranslationKeys)}: <strong>{plan.willDeleteId}</strong>
              </Typography>
              {plan.extraSummary?.map((row) => (
                <Typography key={row.label} variant="body2" sx={{ mb: 1 }}>
                  {row.label}: {row.value}
                </Typography>
              ))}
              {plan.referenceUpdates.length === 0 ? (
                <Alert severity="info" sx={{ mt: 1 }}>
                  {t("No document references found for the discarded entity." as TranslationKeys)}
                </Alert>
              ) : (
                <Table size="small" sx={{ mt: 1 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t("Collection" as TranslationKeys)}</TableCell>
                      <TableCell>{t("Field" as TranslationKeys)}</TableCell>
                      <TableCell align="right">{t("Matches" as TranslationKeys)}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {plan.referenceUpdates.map((update) => (
                      <TableRow key={`${update.collection}.${update.field}`}>
                        <TableCell>{update.collection}</TableCell>
                        <TableCell>{update.field}</TableCell>
                        <TableCell align="right">{update.matchedCount}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Paper>
          )}
        </>
      )}
    </Stack>
  );
}

export default DedupeEntityPanel;
